// Confluence page with events: https://confluence.coop.ch/pages/viewpage.action?pageId=122421534

export class TealiumBaseEvent {
  static readonly EVENT_ACTION_DEFAULT = 'ecommerce';
  static readonly EVENT_CATEGORY_DEFAULT = 'conversions & leads';

  static readonly EVENT_CATEGORY_CONTENT_INTERACTION = 'content interaction';

  product_keys = [
    'product_attributes_brand',
    'product_attributes_basePrice',
    'product_productInfo_productName',
    'product_category_01',
    'product_category_02',
    'product_category_03',
    'product_category_04',
    'product_category_05',
    'product_productInfo_sku',
  ];

  keysWithTypeString = [];

  eventValues = null;

  utag_data = (window as any).utag_data;
  utag = (window as any).utag;
  uTagInfoMap: Map<string, Record<string, any>> = null;

  enableLogs = document.cookie.split('; ').some(cookie => cookie === 'utagdb=true'); // document.cookie="utagdb=true"; // to enable tealium debugger

  constructor(eventType: string, eventCategory: string = TealiumBaseEvent.EVENT_CATEGORY_DEFAULT, eventAction: string = TealiumBaseEvent.EVENT_ACTION_DEFAULT) {
    this.log('TealiumBaseEvent class called!');

    this.eventValues = {
      platform: this.utag_data?.platform,
      event_category: eventCategory,
      event_action: eventAction,
      event_attributes_eventName: eventType,
      event_label: eventType,
    };

    if (this.utag_data?.product_list_name) {
      this.eventValues = { ...this.eventValues, product_list_name: this.utag_data.product_list_name };
    }
  }

  setEventLabel(eventLabel: string) {
    this.eventValues.event_label = eventLabel;
  }

  setEventValue(newValue) {
    this.eventValues = {
      ...this.eventValues,
      ...newValue,
    };
  }

  sanitize(productIdRaw: string): string {
    if (productIdRaw.includes('-')) {
      return productIdRaw.substring(0, productIdRaw.indexOf('-'));
    } else {
      return productIdRaw;
    }
  }

  addTealiumKeyToEvent(uTagKey: string) {
    this.log(`addTealiumKeyToEvent ${uTagKey}`);
    const value = this.getValueFromUTag(uTagKey);
    if (value) {
      this.setEventValue({ [uTagKey]: value });
      this.log(`addedTealiumKeyToEvent ${uTagKey}:${value}`);
    }
  }

  getValueFromUTag(uTagKey: string): string {
    if (uTagKey in this.utag_data) {
      return this.utag_data[uTagKey];
    }
  }

  findProductByField(field: string, value: string) {
    for (const [_key, product] of this.uTagInfoMap) {
      if (product[field]?.includes(value)) {
        return product;
      }
    }
    return null;
  }

  getUTagInfoMapFromProductId(productId: string): Record<string, any> {
    return this.uTagInfoMap.get(productId);
  }

  async executeLink(productIdRaw: string = null, additionalEventInfo = null): Promise<void> {
    if (!this.utag) {
      this.warn(`No uTag found. Event ${this.eventValues.event_attributes_eventName}`);
      return;
    }

    if (productIdRaw && !this.uTagInfoMap) {
      this.warn(`No uTagInfoMapInfo found for productId ${productIdRaw}. Event ${this.eventValues.event_attributes_eventName}`);
      return;
    }

    let utagInfoToBeSent = this.eventValues;

    if (this.uTagInfoMap && productIdRaw) {
      const productId = this.sanitize(productIdRaw);
      const uTagInfoMapProductInfo = this.getUTagInfoMapFromProductId(productId);

      if (uTagInfoMapProductInfo) {
        utagInfoToBeSent = {
          ...utagInfoToBeSent,
          ...uTagInfoMapProductInfo,
        };
      } else {
        this.warn(`No uTagInfoMapInfo found for productIdRaw ${productIdRaw} / productId ${productId}. Event ${this.eventValues.event_attributes_eventName}`);
        return;
      }
    }

    if (additionalEventInfo) {
      utagInfoToBeSent = { ...utagInfoToBeSent, ...additionalEventInfo };
    }

    if (this.enableLogs) {
      this.log('Info sent to uTag');
      console.table(utagInfoToBeSent);
    }

    this.utag.link(utagInfoToBeSent);
  }

  async addClickEvent(htmlElements: HTMLElement[], productIdAttr: string, additionalEventInfo = null) {
    const handleClick = (htmlElement: HTMLElement, additionalEventInfo) => {
      const productId = htmlElement.getAttribute(productIdAttr);
      return async (_event: MouseEvent) => {
        this.log('handleClick triggered');
        this.executeLink(productId, additionalEventInfo);
      };
    };

    htmlElements.forEach((htmlElement: HTMLElement) => {
      this.log('add addEventListener for Tealium');
      htmlElement.addEventListener('click', handleClick(htmlElement, additionalEventInfo));
    });
  }

  buildUTagProductInfoMap(): Map<string, Record<string, any>> {
    if (this.utag && this.utag_data?.product_productInfo_sku) {
      const uTagInfoMap = new Map<string, Record<string, any>>();

      this.utag_data.product_productInfo_sku.forEach((sku: string, index: number) => {
        const productInfo: Record<string, any> = {};

        this.product_keys.forEach(key => {
          if (key in this.utag_data) {
            if (this.keysWithTypeString.includes(key)) {
              productInfo[key] = this.utag_data[key][index];
            } else {
              productInfo[key] = [this.utag_data[key][index]];
            }
          } else {
            productInfo[key] = [''];
          }
        });
        uTagInfoMap.set(sku, productInfo);
      });
      return uTagInfoMap;
    }
    return null;
  }

  log(mesage: string) {
    if (this.enableLogs) console.log(`TealiumBaseEvent - Event ${this.eventValues?.event_attributes_eventName} - ${mesage}`, this.getTS());
  }

  warn(mesage: string) {
    console.warn('TealiumBaseEvent - ' + mesage, this.getTS());
  }

  getTS(): string {
    const now = new Date();
    return now.toLocaleString() + '.' + now.getMilliseconds();
  }
}
